
import React, { createContext, useContext, ReactNode } from 'react';
import * as Sentry from '@/lib/sentry-shim';
import { useToast } from '@/hooks/use-toast';
import { useSession } from '@/hooks/useSession';
import { AuthContextType } from '@/types/auth';
import {
  signInWithPassword,
  signUpWithPassword,
  signOutUser,
  resetUserPassword,
  resendConfirmationEmail,
  verifyEmailOtp,
} from '@/lib/auth';
import { isMissingCredentials } from '@/lib/supabase';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, session, isLoading, isAuthenticated, isAuthReady } = useSession();
  const { toast } = useToast();

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithPassword(email, password);
      toast({
        title: 'Welcome back',
        description: 'You have successfully signed in',
      });
    } catch (error: any) {
      // Report to Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'AuthContext',
          action: 'signIn',
        },
        extra: {
          email,
          errorCode: error.code,
          errorMessage: error.message,
          errorStatus: error.status,
        },
      });
      
      toast({
        title: 'Sign In Error',
        description: error.message || 'Failed to sign in',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const signUp = async (email: string, password: string, metadata = {}) => {
    try {
      console.log('AuthContext: Starting sign up process for', email);
      const result = await signUpWithPassword(email, password, metadata);

      if (result?.needsVerification) {
        console.log('AuthContext: Sign up needs email verification for', result.email);
        toast({
          title: 'Verification code sent',
          description: 'Please check your email for a 6-digit code.',
        });
        return result;
      }

      console.log('AuthContext: Sign up successful, user is now authenticated');
      toast({
        title: 'Welcome to Discloser!',
        description: 'Your account has been created.',
      });
      return result;
    } catch (error: any) {
      console.error('AuthContext: Sign up error', error);
      
      // Check if this is a duplicate email error
      const errorCode = typeof error?.code === 'string' ? error.code.toLowerCase() : '';
      const errorMessage = error?.message ?? '';
      const normalizedMessage = errorMessage.toLowerCase();
      
      const isDuplicateEmail = 
        errorCode === 'user_already_registered' ||
        errorCode === 'user_already_exists' ||
        errorCode === 'signup_disabled' ||
        normalizedMessage.includes('already registered') ||
        normalizedMessage.includes('already exists') ||
        normalizedMessage.includes('email taken') ||
        normalizedMessage.includes('user already');
      
      // Report to Sentry with detailed context
      Sentry.captureException(error, {
        tags: {
          component: 'AuthContext',
          action: 'signUp',
          isDuplicateEmail: isDuplicateEmail ? 'true' : 'false',
        },
        extra: {
          email,
          hasMetadata: !!metadata && Object.keys(metadata).length > 0,
          metadataKeys: metadata ? Object.keys(metadata) : [],
          errorCode: error.code,
          errorMessage: error.message,
          errorStatus: error.status,
          errorDetails: error.details,
          isDuplicateEmail,
        },
      });
      
      // For duplicate email errors, don't show a toast here - let Auth.tsx handle it with Alert
      // Only show toast for other errors
      if (!isDuplicateEmail) {
        toast({
          title: 'Sign Up Error',
          description: error.message || 'Failed to sign up. Please try again.',
          variant: 'destructive',
        });
      }
      
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await signOutUser();
      toast({
        title: 'Signed out',
        description: 'You have been signed out successfully',
      });
    } catch (error: any) {
      // Report to Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'AuthContext',
          action: 'signOut',
        },
        extra: {
          userId: user?.id,
          errorCode: error.code,
          errorMessage: error.message,
        },
      });
      
      toast({
        title: 'Sign Out Error',
        description: error.message || 'Failed to sign out',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await resetUserPassword(email);
      toast({
        title: 'Password Reset Email Sent',
        description: 'Check your email for a link to reset your password',
      });
    } catch (error: any) {
      // Report to Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'AuthContext',
          action: 'resetPassword',
        },
        extra: {
          email,
          errorCode: error.code,
          errorMessage: error.message,
          errorStatus: error.status,
        },
      });
      
      toast({
        title: 'Password Reset Failed',
        description: error.message || 'Failed to send reset password email',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const verifyOtp = async (email: string, token: string) => {
    try {
      await verifyEmailOtp(email, token);
      toast({
        title: 'Email verified',
        description: 'Your account has been confirmed.',
      });
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: { component: 'AuthContext', action: 'verifyOtp' },
        extra: { email, errorCode: error.code, errorMessage: error.message },
      });
      toast({
        title: 'Verification failed',
        description: error.message || 'Invalid or expired code. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const resendOtp = async (email: string) => {
    try {
      await resendConfirmationEmail(email);
      toast({
        title: 'Code resent',
        description: 'A new verification code has been sent to your email.',
      });
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: { component: 'AuthContext', action: 'resendOtp' },
        extra: { email, errorCode: error.code, errorMessage: error.message },
      });
      toast({
        title: 'Resend failed',
        description: error.message || 'Failed to resend verification code.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const value = {
    user,
    session,
    isLoading,
    isAuthenticated,
    isAuthReady,
    signIn,
    signUp,
    signOut,
    resetPassword,
    verifyOtp,
    resendOtp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
